import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Clock, Mail, Play, RefreshCw, AlertTriangle, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import { BACKEND_URL } from '../../config';
const API = BACKEND_URL;

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-white">{value}</p>
      </div>
    </div>
  );
}

export default function DevBilling() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simDays, setSimDays] = useState('7');
  const [testEmail, setTestEmail] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/dev/collections/tenants`, { params: { limit: 500 }, withCredentials: true })
      .then(r => {
        const docs = r.data?.documents || r.data || [];
        setTenants(docs);
        if (docs.length > 0) setSelectedTenant(docs[0].tenant_id);
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || err.message || 'Unknown error';
        toast.error(`Failed to load tenants: ${msg}`);
      });
  }, []);

  const loadSubscription = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const filter = JSON.stringify([{ field: "tenant_id", operator: "equals", value: selectedTenant }]);
      const res = await axios.get(`${API}/api/dev/collections/subscriptions`, { params: { filter }, withCredentials: true });
      const docs = res.data?.documents || res.data || [];
      setSubscription(docs[0] || null);
    } catch (err) {
      toast.error(`Failed to load subscription: ${err.response?.data?.detail || err.message}`);
    }
    // Load email logs for this tenant
    try {
      const filter = JSON.stringify([{ field: "tenant_id", operator: "equals", value: selectedTenant }]);
      const res = await axios.get(`${API}/api/dev/collections/email_logs`, { params: { filter }, withCredentials: true });
      setEmailLogs(res.data?.documents || res.data || []);
    } catch {
      setEmailLogs([]);
    }
    setLoading(false);
  }, [selectedTenant]);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  const simulateTrial = async (action) => {
    setSending(true);
    try {
      await axios.post(`${API}/api/dev/billing/simulate`, {
        tenant_id: selectedTenant,
        action,
        days: parseInt(simDays) || 7,
      }, { withCredentials: true });
      toast.success(`Trial state updated: ${action}`);
      await loadSubscription();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Simulation failed');
    }
    setSending(false);
  };

  const triggerReminders = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/api/admin/trial-reminders/trigger`, {}, { withCredentials: true });
      toast.success('Trial reminder check completed');
      await loadSubscription();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to trigger reminders');
    }
    setTriggering(false);
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email address'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/email/test`, { to: testEmail }, { withCredentials: true });
      if (res.data?.success) toast.success(`Test email sent to ${testEmail}`);
      else toast.error(res.data?.error || 'Email failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    }
    setSending(false);
  };

  const trialDays = subscription?.trial_end
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end) - Date.now()) / 86400000))
    : null;

  const statusColor = {
    trialing: 'text-blue-400',
    trial_expired: 'text-red-400',
    active: 'text-emerald-400',
    cancelled: 'text-amber-400',
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="dev-billing-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Billing & Trials</h1>
            <p className="text-xs text-slate-400">Simulate trial states and test reminder emails</p>
          </div>
        </div>
        <a
          href="https://dashboard.stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-[#635BFF]/10 hover:bg-[#635BFF]/20 text-[#635BFF] text-xs font-medium px-3 py-2 rounded-lg transition-colors"
          data-testid="stripe-dashboard-link"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/></svg>
          Stripe Dashboard
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Tenant Selector */}
      <div className="mb-6">
        <label className="text-xs text-slate-400 mb-1.5 block">Select Tenant</label>
        <Select value={selectedTenant} onValueChange={setSelectedTenant}>
          <SelectTrigger className="w-72 bg-slate-800 border-slate-700 text-white" data-testid="billing-tenant-select">
            <SelectValue placeholder="Select a tenant" />
          </SelectTrigger>
          <SelectContent>
            {tenants.map(t => (
              <SelectItem key={t.tenant_id} value={t.tenant_id}>
                {t.name || t.tenant_id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading subscription...
        </div>
      ) : (
        <>
          {/* Subscription Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard
              label="Status"
              value={subscription?.status || 'No subscription'}
              icon={subscription?.status === 'active' ? CheckCircle2 : AlertTriangle}
              color={subscription?.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}
            />
            <StatCard label="Plan" value={subscription?.plan || 'N/A'} icon={CreditCard} color="bg-blue-500/20 text-blue-400" />
            <StatCard label="Trial Days Left" value={trialDays !== null ? trialDays : 'N/A'} icon={Clock} color="bg-purple-500/20 text-purple-400" />
            <StatCard
              label="Reminders Sent"
              value={(subscription?.reminders_sent || []).join(', ') || 'None'}
              icon={Mail}
              color="bg-rose-500/20 text-rose-400"
            />
          </div>

          {subscription && (
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-white mb-1">Subscription Details</h3>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                <span className="text-slate-400">Tenant ID:</span>
                <span className="text-slate-200 font-mono">{subscription.tenant_id}</span>
                <span className="text-slate-400">Status:</span>
                <span className={statusColor[subscription.status] || 'text-slate-200'}>{subscription.status}</span>
                <span className="text-slate-400">Trial Start:</span>
                <span className="text-slate-200">{subscription.trial_start ? new Date(subscription.trial_start).toLocaleDateString() : 'N/A'}</span>
                <span className="text-slate-400">Trial End:</span>
                <span className="text-slate-200">{subscription.trial_end ? new Date(subscription.trial_end).toLocaleDateString() : 'N/A'}</span>
                <span className="text-slate-400">Created:</span>
                <span className="text-slate-200">{subscription.created_at ? new Date(subscription.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          )}

          {/* Trial Simulation */}
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Play className="w-4 h-4 text-emerald-400" />
              Trial Simulation
            </h3>
            <p className="text-xs text-slate-400 mb-4">Modify the trial end date to simulate different states, then trigger the reminder check.</p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block">Days remaining</label>
                <Input
                  type="number"
                  value={simDays}
                  onChange={e => setSimDays(e.target.value)}
                  className="w-24 bg-slate-900 border-slate-600 text-white h-9 text-sm"
                  min="0"
                  data-testid="sim-days-input"
                />
              </div>
              <Button
                size="sm"
                onClick={() => simulateTrial('set_days')}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                data-testid="sim-set-days-btn"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Set Trial Days
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => simulateTrial('expire_now')}
                disabled={sending}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-9"
                data-testid="sim-expire-btn"
              >
                Expire Now
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => simulateTrial('reset_trial')}
                disabled={sending}
                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 h-9"
                data-testid="sim-reset-btn"
              >
                Reset 30-Day Trial
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => simulateTrial('clear_reminders')}
                disabled={sending}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 h-9"
                data-testid="sim-clear-reminders-btn"
              >
                Clear Reminders
              </Button>
            </div>
          </div>

          {/* Trigger Reminders */}
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4 text-rose-400" />
              Email Reminders
            </h3>
            <p className="text-xs text-slate-400 mb-4">Run the trial reminder check manually. Emails will be sent to tenant admins based on their trial state (7d, 3d, expired).</p>
            <div className="flex flex-wrap items-end gap-3">
              <Button
                size="sm"
                onClick={triggerReminders}
                disabled={triggering}
                className="bg-rose-600 hover:bg-rose-700 text-white h-9"
                data-testid="trigger-reminders-btn"
              >
                {triggering ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Run Reminder Check
              </Button>
            </div>
          </div>

          {/* Test Email */}
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-400" />
              Test Email (Resend)
            </h3>
            <p className="text-xs text-slate-400 mb-4">Send a test email to verify Resend integration.</p>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <label className="text-[11px] text-slate-500 mb-1 block">Recipient email</label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-slate-900 border-slate-600 text-white h-9 text-sm"
                  data-testid="test-email-input"
                />
              </div>
              <Button
                size="sm"
                onClick={sendTestEmail}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                data-testid="send-test-email-btn"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Send Test
              </Button>
            </div>
          </div>

          {/* Email Logs */}
          {emailLogs.length > 0 && (
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Email Log</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {emailLogs.slice(0, 20).map((log, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-slate-900/60 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${log.success ? 'bg-emerald-400' : 'bg-red-400'}`} />
                      <span className="text-slate-300">{log.to}</span>
                      <span className="text-slate-500">{log.type}</span>
                    </div>
                    <span className="text-slate-500">{log.sent_at ? new Date(log.sent_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
